import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import Conversations from '../Conversations';
import Icon from '../components/icons/Icon';
import './Memories.css';

const parseUtcDate = (dateString) => {
  if (!dateString) return new Date(0);
  const normalized = /Z|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : dateString + 'Z';
  return new Date(normalized);
};

const getConvTypeBadge = (conv) => {
  if (conv.appName && conv.appName !== 'hub') return { label: conv.appName, type: 'playbook' };
  if (conv.mode === 'code') return { label: 'Code', type: 'code' };
  return { label: 'Chat', type: 'chat' };
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = parseUtcDate(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return 'Last week';
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return 'Last month';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  if (diffYears === 1) return 'Last year';
  return `${diffYears} years ago`;
};

function Memories() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const result = await Conversations.searchConversations('');
      if (result.success) {
        const sorted = result.results.sort((a, b) => {
          return parseUtcDate(b.updatedAt || b.createdAt) - parseUtcDate(a.updatedAt || a.createdAt);
        });
        const unique = [];
        const seen = new Set();
        for (const conv of sorted) {
          if (!seen.has(conv._id)) {
            seen.add(conv._id);
            unique.push(conv);
          }
        }
        setConversations(unique);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleSelectConversation = (conv) => {
    if (editingId) return;
    if (conv.mode === 'code') {
      const base = (conv.tenantId && conv.projectId)
        ? `/chat/${conv.tenantId}/${conv.projectId}`
        : '/chat';
      navigate(`${base}?conversationId=${conv._id}`);
    } else {
      navigate(`/compose?conversationId=${conv._id}`);
    }
  };

  const handleNewChat = () => {
    navigate('/compose');
  };

  const handleEditClick = (e, conv) => {
    e.stopPropagation();
    setDeletingId(null);
    setEditingId(conv._id);
    setEditingName(conv.conversationName || '');
  };

  const handleEditCancel = (e) => {
    e && e.stopPropagation();
    setEditingId(null);
    setEditingName('');
  };

  const handleEditConfirm = async (e) => {
    e && e.stopPropagation();
    const name = editingName.trim();
    if (!name || !editingId) { handleEditCancel(); return; }
    setSavingId(editingId);
    const result = await Conversations.updateConversation(editingId, {conversationName: name});
    if (result.success) {
      setConversations(prev => prev.map(c => c._id === editingId ? {...c, conversationName: name} : c));
    }
    setSavingId(null);
    setEditingId(null);
    setEditingName('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') handleEditConfirm(e);
    if (e.key === 'Escape') handleEditCancel(e);
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingName('');
    setDeletingId(id);
  };

  const handleDeleteCancel = (e) => {
    e && e.stopPropagation();
    setDeletingId(null);
  };

  const handleDeleteConfirm = async (e, id) => {
    e && e.stopPropagation();
    const result = await Conversations.deleteConversation(id);
    if (result.success) {
      setConversations(prev => prev.filter(c => c._id !== id));
    }
    setDeletingId(null);
  };

  const displayed = searchQuery.trim()
    ? conversations.filter(conv =>
        (conv.conversationName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  return (
    <div className="memories-page">
      <div className="memories-header">
        <div className="memories-title-row">
          <Icon name="clock" size={22} />
          <h1 className="memories-title">Memories</h1>
        </div>
        <p className="memories-subtitle">Your chat history — browse, rename or continue past conversations.</p>
      </div>

      <div className="memories-toolbar">
        <div className="memories-search-wrapper">
          <Icon name="search" size={16} />
          <input
            className="memories-search-input"
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            type="text"
            value={searchQuery}
          />
          {searchQuery && (
            <button
              className="memories-search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
              type="button"
            >
              <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                <line x1="18" x2="6" y1="6" y2="18"/>
                <line x1="6" x2="18" y1="6" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        <button className="memories-new-btn" onClick={handleNewChat} type="button">
          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
            <line x1="12" x2="12" y1="5" y2="19"/>
            <line x1="5" x2="19" y1="12" y2="12"/>
          </svg>
          New chat
        </button>
      </div>

      <div className="memories-content">
        {loading ? (
          <div className="memories-loading">
            <div className="memories-spinner" />
            <span>Loading conversations…</span>
          </div>
        ) : displayed.length === 0 ? (
          <div className="memories-empty">
            <Icon name="clock" size={40} />
            <p>{searchQuery.trim() ? 'No matching conversations' : 'No conversations yet'}</p>
            {!searchQuery.trim() && (
              <button className="memories-new-btn" onClick={handleNewChat} type="button">
                Start your first chat
              </button>
            )}
          </div>
        ) : (
          <div className="memories-list">
            {displayed.map((conv) => (
              <div
                key={conv._id}
                className={`memory-item${editingId === conv._id ? ' editing' : ''}${deletingId === conv._id ? ' deleting' : ''}`}
              >
                <button
                  className="memory-item-main"
                  disabled={editingId === conv._id || deletingId === conv._id}
                  onClick={() => handleSelectConversation(conv)}
                  type="button"
                >
                  <div className="memory-item-info">
                    {editingId === conv._id ? (
                      <input
                        autoFocus
                        className="memory-rename-input"
                        onChange={(e) => setEditingName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleEditKeyDown}
                        type="text"
                        value={editingName}
                      />
                    ) : (
                      <div className="memory-name-row">
                        <div className="memory-name">{conv.conversationName || 'Untitled Conversation'}</div>
                        {(() => { const badge = getConvTypeBadge(conv); return <span className={`memory-type-badge memory-type-badge--${badge.type}`}>{badge.label}</span>; })()}
                      </div>
                    )}
                    {editingId !== conv._id && conv.mode === 'code' && conv.projectName && (
                      <div className="memory-project">{conv.projectName}</div>
                    )}
                    {editingId !== conv._id && conv.messages?.[0]?.text && (
                      <div className="memory-preview">
                        {conv.messages[0].text.substring(0, 80)}
                        {conv.messages[0].text.length > 80 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  <div className="memory-date" title={parseUtcDate(conv.updatedAt || conv.createdAt).toLocaleString()}>
                    {formatDate(conv.updatedAt || conv.createdAt)}
                  </div>
                </button>

                <div className="memory-actions">
                  {editingId === conv._id ? (
                    <>
                      <button className="mem-action-btn mem-action-confirm" disabled={savingId === conv._id} onClick={handleEditConfirm} title="Confirm" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </button>
                      <button className="mem-action-btn mem-action-cancel" onClick={handleEditCancel} title="Cancel" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                          <line x1="18" x2="6" y1="6" y2="18"/>
                          <line x1="6" x2="18" y1="6" y2="18"/>
                        </svg>
                      </button>
                    </>
                  ) : deletingId === conv._id ? (
                    <>
                      <button className="mem-action-btn mem-action-confirm mem-action-delete-confirm" onClick={(e) => handleDeleteConfirm(e, conv._id)} title="Confirm delete" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </button>
                      <button className="mem-action-btn mem-action-cancel" onClick={handleDeleteCancel} title="Cancel" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" width="14">
                          <line x1="18" x2="6" y1="6" y2="18"/>
                          <line x1="6" x2="18" y1="6" y2="18"/>
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="mem-action-btn mem-action-edit" onClick={(e) => handleEditClick(e, conv)} title="Rename" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button className="mem-action-btn mem-action-delete" onClick={(e) => handleDeleteClick(e, conv._id)} title="Delete" type="button">
                        <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                          <path d="M10 11v6"/>
                          <path d="M14 11v6"/>
                          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Memories;
